// RepeatableInput — FE-310: Repeatable input fields with + Add button
// E.g., "Transaction creation" — user can add multiple transaction entries
// Manages a local array of string values; each row has an input + remove button
// The "+ Add" button appends a new empty row

import React, { useRef, useState } from "react";
import { TextInput, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import type { SubtaskItem } from "../types";

interface RepeatableInputProps {
  item: SubtaskItem;
  disabled?: boolean;
}

// Repeatable values are stored on the BE as a JSON-encoded string in `value`.
function parseInitialValues(raw: string | boolean | undefined): string[] {
  if (typeof raw !== "string" || raw === "") return [""];
  try {
    // JSON.parse returns `any`; narrow to unknown then let the guard narrow
    // to string[] via Array.isArray + every(typeof === "string").
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === "string")) {
      return parsed.length > 0 ? parsed : [""];
    }
  } catch {
    // Legacy single-value entries
  }
  return [raw];
}

// Stable client-side row id — used as the React key so removing a middle row
// doesn't shift other rows' DOM nodes (which would carry over cursor / focus /
// IME state to the wrong input). The id is local to the component; the BE only
// ever sees the serialised string[] of values.
interface Row {
  key: number;
  value: string;
}

export const RepeatableInput: React.FC<RepeatableInputProps> = ({ item, disabled = false }) => {
  const initialValues = parseInitialValues(item.value);
  // Monotonic counter that survives re-renders; each row gets a unique key.
  const idSeqRef = useRef(0);
  const [rows, setRows] = useState<Row[]>(() =>
    initialValues.map((value) => ({ key: idSeqRef.current++, value })),
  );
  const lastSavedRef = useRef<string>(JSON.stringify(initialValues));
  const { updateSubtaskValue } = useSubtaskActionsContext();

  const config = item.config || {};
  const inputType = (config.inputType as string) || "text";
  const placeholder = (config.placeholder as string) || "";
  const suffix = config.suffix as string | undefined;
  const addLabel = (config.addLabel as string) || "+ Add";

  const persist = (next: Row[]) => {
    const serialized = JSON.stringify(next.map((r) => r.value));
    if (serialized === lastSavedRef.current) return;
    lastSavedRef.current = serialized;
    void updateSubtaskValue(item.id, serialized, !!item.isCompleted);
  };

  const handleChange = (index: number, newValue: string) => {
    if (disabled) return;
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value: newValue };
      return next;
    });
  };

  const handleBlur = () => {
    if (disabled) return;
    persist(rows);
  };

  // Compute the next array outside the updater so the side effect runs exactly
  // once even under React StrictMode's intentional double-invocation of state
  // updater callbacks (updaters must be pure).
  const handleAdd = () => {
    if (disabled) return;
    const next: Row[] = [...rows, { key: idSeqRef.current++, value: "" }];
    setRows(next);
    persist(next);
  };

  const handleRemove = (index: number) => {
    if (disabled) return;
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    persist(next);
  };

  return (
    <div className={styles.repeatableInputGroup}>
      <span className={styles.repeatableInputLabel}>{item.label}</span>

      {rows.map((row, idx) => (
        <div key={row.key} className={styles.repeatableInputRow} onBlur={handleBlur}>
          <TextInput
            label=""
            value={row.value}
            onSetValue={(v) => handleChange(idx, v)}
            placeholder={placeholder}
            disabled={disabled}
            type={inputType === "number" || inputType === "currency" ? "number" : "text"}
            isFullWidth
          />
          {suffix && (
            <Text
              variant="secondary-sm"
              color={Colors.Neutral500}
              className={styles.inputSuffix}
            >
              {suffix}
            </Text>
          )}
          {rows.length > 1 && !disabled && (
            <button
              type="button"
              className={styles.repeatableRemoveButton}
              onClick={() => handleRemove(idx)}
              aria-label="Remove"
            >
              <Icon type="xmark" size="small" color={Colors.Neutral500} />
            </button>
          )}
        </div>
      ))}

      {!disabled && (
        <button
          type="button"
          className={styles.repeatableAddButton}
          onClick={handleAdd}
          data-testid={`repeatable-add-${item.id}`}
        >
          {addLabel}
        </button>
      )}

      {item.helpText && (
        <Text variant="secondary-sm" color={Colors.Neutral500} className={styles.helpText}>
          {item.helpText}
        </Text>
      )}
    </div>
  );
};

export default RepeatableInput;
